// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// Admin API for PLATFORM-scope New Business Application templates.
// See docs/specs/new-business-application-prd.md §5.6. Backed by
// /api/new-business/admin/templates/**.
//
// Types are shared with the Advisor UI via @loa/new-business-form so the two
// authoring surfaces never drift.

import type {
  AnnotationBinding,
  BindingCatalogue,
  NewBusinessTemplate,
  NewBusinessTemplateEditorApi,
} from '@loa/new-business-form';

export type { AnnotationBinding, BindingCatalogue, NewBusinessTemplate };

const base = '/api/new-business/admin/templates';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const newBusinessTemplateApi = {
  list: (providerId: string): Promise<NewBusinessTemplate[]> =>
    request(`${base}/providers/${providerId}`),
  get: (templateId: string): Promise<NewBusinessTemplate> =>
    request(`${base}/${templateId}`),
  bindingCatalogue: (): Promise<BindingCatalogue> =>
    request(`${base}/binding-catalogue`),
  create: (body: NewBusinessTemplate): Promise<NewBusinessTemplate> =>
    request(base, { method: 'POST', body: JSON.stringify(body) }),
  update: (templateId: string, body: NewBusinessTemplate): Promise<NewBusinessTemplate> =>
    request(`${base}/${templateId}`, { method: 'PUT', body: JSON.stringify(body) }),
  activate: (templateId: string): Promise<NewBusinessTemplate> =>
    request(`${base}/${templateId}/activate`, { method: 'POST' }),
  deactivate: (templateId: string): Promise<NewBusinessTemplate> =>
    request(`${base}/${templateId}/deactivate`, { method: 'POST' }),
  remove: (templateId: string): Promise<void> =>
    request(`${base}/${templateId}`, { method: 'DELETE' }),

  /** Multipart PDF upload — no Content-Type header (browser sets the boundary). */
  uploadPdf: async (templateId: string, file: File): Promise<NewBusinessTemplate> => {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(`${base}/${templateId}/pdf`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
    }
    return response.json() as Promise<NewBusinessTemplate>;
  },

  /** The blank provider-form PDF for the annotation editor (fetched as a Blob). */
  fetchPdf: async (templateId: string): Promise<ArrayBuffer> => {
    const response = await fetch(`${base}/${templateId}/pdf`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.arrayBuffer();
  },
};

/**
 * The subset of the admin API the shared {@link NewBusinessTemplateEditor}
 * needs, adapted to its transport-agnostic contract.
 */
export const newBusinessTemplateEditorApi: NewBusinessTemplateEditorApi = {
  fetchPdf: newBusinessTemplateApi.fetchPdf,
  uploadPdf: newBusinessTemplateApi.uploadPdf,
  update: newBusinessTemplateApi.update,
  activate: newBusinessTemplateApi.activate,
  deactivate: newBusinessTemplateApi.deactivate,
  bindingCatalogue: newBusinessTemplateApi.bindingCatalogue,
};
